import { motion } from 'framer-motion';
import { fadeInUp } from '../lib/animations';
import SEOPageLayout from '../components/seo/SEOPageLayout';

const relatedLinks = [
  { to: '/perte-information-entreprise', label: 'Ce que la perte d\'information coûte à votre entreprise' },
  { to: '/centraliser-information-entreprise', label: 'Pourquoi centraliser ne suffit pas' },
  { to: '/memoire-entreprise-futur-travail', label: 'La mémoire d\'entreprise : le futur du travail' },
];

const ProductiviteEquipe = () => {
  return (
    <SEOPageLayout
      title="Comment vos équipes perdent 30% de leur temps sans le savoir"
      metaTitle="Productivité équipe : l'outil qui change tout | Cloneia"
      metaDescription="Chercher, demander, attendre : la productivité de vos équipes est freinée par l'accès à l'information. Découvrez une nouvelle approche."
      url="/productivite-equipe-outil"
      readingTime={4}
      subtitle="Chercher n'est pas travailler."
      relatedLinks={relatedLinks}
    >
      <article className="space-y-12 text-[#a1a1aa] leading-relaxed" data-testid="seo-article-productivite">

        {/* Section 1 */}
        <motion.div {...fadeInUp}>
          <h2 className="text-2xl font-semibold text-[#f5f5f5] mb-4">
            30% du temps de travail perdu
          </h2>
          <p>
            Vos équipes sont compétentes. Vos outils sont en place. Et pourtant, la productivité n'est pas au rendez-vous.
          </p>
          <p className="mt-4">
            La raison est simple : une part massive du temps de travail est consacrée à chercher de l'information. Pas à l'utiliser. À la chercher.
          </p>
          <p className="mt-4 text-[#f5f5f5] font-medium">
            Chercher un email. Appeler un collègue. Attendre une réponse. Retrouver un document. Reconstituer un historique.
          </p>
        </motion.div>

        {/* Section 2 */}
        <motion.div {...fadeInUp}>
          <h2 className="text-2xl font-semibold text-[#f5f5f5] mb-4">
            Le cercle vicieux de l'interruption
          </h2>
          <p>
            Pour retrouver une information, le réflexe naturel est de demander à quelqu'un. Ce qui crée une interruption. Pour deux personnes.
          </p>
          <div className="mt-6 p-6 rounded-xl border border-white/5 bg-[#1a1a2e]/30">
            <p className="text-[#f5f5f5] font-medium mb-2">L'effet domino</p>
            <p>
              Marie cherche une info sur un client. Elle appelle Thomas. Thomas est en réunion. Marie attend. Thomas rappelle. Marie est passée à autre chose. Deux personnes, une heure perdue. Pour une information qui existait déjà.
            </p>
          </div>
          <p className="mt-4">
            Ce scénario se répète des dizaines de fois par jour. C'est invisible, mais le coût est colossal.
          </p>
        </motion.div>

        {/* Section 3 */}
        <motion.div {...fadeInUp}>
          <h2 className="text-2xl font-semibold text-[#f5f5f5] mb-4">
            Les vrais freins à la productivité
          </h2>
          <div className="space-y-4">
            <p>
              <span className="text-[#f5f5f5] font-medium">La recherche dispersée</span> — l'information est dans 10 outils différents. Trouver la bonne pièce du puzzle prend du temps.
            </p>
            <p>
              <span className="text-[#f5f5f5] font-medium">La dépendance aux personnes</span> — certaines informations n'existent que dans la tête d'un collègue. S'il n'est pas disponible, vous êtes bloqué.
            </p>
            <p>
              <span className="text-[#f5f5f5] font-medium">Le manque de contexte</span> — vous trouvez un bout d'information, mais sans le contexte qui l'entoure. Vous devez reconstituer l'historique.
            </p>
          </div>
        </motion.div>

        {/* Section 4 */}
        <motion.div {...fadeInUp}>
          <h2 className="text-2xl font-semibold text-[#f5f5f5] mb-4">
            Ce que ça devrait être
          </h2>
          <p>
            La productivité, c'est simple : passer le maximum de temps à agir, et le minimum à chercher.
          </p>
          <p className="mt-4">
            Idéalement, quand vous avez besoin d'une information, vous devriez pouvoir poser la question et avoir la réponse. Immédiatement. Sans chercher. Sans dépendre de personne.
          </p>
        </motion.div>

        {/* Section 5 - Cloneia */}
        <motion.div {...fadeInUp}>
          <h2 className="text-2xl font-semibold text-[#f5f5f5] mb-4">
            Passer de la recherche à la réponse
          </h2>
          <p>
            Cloneia crée un clone mémoire de chaque collaborateur. L'information de tous les échanges est captée automatiquement et rendue accessible en quelques secondes.
          </p>
          <p className="mt-4 text-[#f5f5f5] font-medium">
            Plus besoin de chercher. Plus besoin de demander. Plus besoin d'attendre.
          </p>
          <p className="mt-4">
            Ce qui prenait 30 minutes prend maintenant quelques secondes. Une question, une réponse.
          </p>
        </motion.div>

        {/* Section 6 */}
        <motion.div {...fadeInUp}>
          <h2 className="text-2xl font-semibold text-[#f5f5f5] mb-4">
            L'impact sur vos équipes
          </h2>
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#3b82f6]/10 border border-[#3b82f6]/20 flex items-center justify-center flex-shrink-0">
                <span className="text-[#3b82f6] font-bold">x5</span>
              </div>
              <div>
                <p className="text-[#f5f5f5] font-medium">Plus rapide</p>
                <p className="text-sm">Retrouver une information complexe passe de 30 minutes à quelques secondes.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#3b82f6]/10 border border-[#3b82f6]/20 flex items-center justify-center flex-shrink-0">
                <span className="text-[#3b82f6] font-bold">0</span>
              </div>
              <div>
                <p className="text-[#f5f5f5] font-medium">Interruptions</p>
                <p className="text-sm">Plus besoin de déranger un collègue pour obtenir une information.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#3b82f6]/10 border border-[#3b82f6]/20 flex items-center justify-center flex-shrink-0">
                <span className="text-[#3b82f6] font-bold">24/7</span>
              </div>
              <div>
                <p className="text-[#f5f5f5] font-medium">Toujours disponible</p>
                <p className="text-sm">L'information est accessible à tout moment, même en dehors des heures de bureau.</p>
              </div>
            </div>
          </div>
        </motion.div>

      </article>
    </SEOPageLayout>
  );
};

export default ProductiviteEquipe;
